// Contas da tela de Horários (repaginada em 25/10/2026). Sem tela, sem rede.
//
// O servidor guarda cada dia com até 3 períodos fixos (opening_time/closing_time,
// _2 e _3, ligados por has_second_period/has_third_period) e o 24 horas à parte.
// A tela trabalha com uma lista de períodos por dia e converte na ida e na volta.
//
// day_of_week do servidor: 0 = segunda … 6 = domingo (weekday do Python).
// A tela mostra a semana começando no domingo.

export const DIAS = ["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado", "Domingo"];
export const SIGLAS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
export const ORDEM = [6, 0, 1, 2, 3, 4, 5];
export const DIAS_UTEIS = [0, 1, 2, 3, 4];
export const MAX_PERIODOS = 3;

/** Horários de pico de referência para restaurante (faixas na grade e cards de Desempenho). */
export const PICOS = [
  { nome: "almoço", de: 11, ate: 15 },
  { nome: "jantar", de: 18, ate: 23 },
];

const pad = (n) => String(n).padStart(2, "0");
export const paraMin = (t) => {
  const [h, m] = String(t || "").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};
/** "23:59" é o fim do dia: conta como 24:00 nas contas. */
const fimMin = (t) => (t === "23:59" ? 1440 : paraMin(t));
export const hm = (m) => (m >= 1439 ? "23:59" : `${pad(Math.floor(m / 60))}:${pad(m % 60)}`);

/** 90 → "1h30"; 240 → "4h"; 45 → "45min". */
export function duracao(m) {
  m = Math.round(m);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r}min`;
  return r ? `${h}h${pad(r)}` : `${h}h`;
}
export const horas = (m) => (m % 60 === 0 ? `${m / 60} ${m / 60 === 1 ? "hora" : "horas"}` : duracao(m));

/**
 * O que a pessoa digitou → "HH:MM", ou null se não for hora.
 * "9" → 09:00, "930" → 09:30, "1800" → 18:00, "24"/"2400" → 23:59.
 */
export function normalizarHora(v) {
  const d = String(v || "").replace(/\D/g, "");
  if (!d) return null;
  let h;
  let m;
  if (d.length <= 2) { h = +d; m = 0; } else if (d.length === 3) { h = +d.slice(0, 1); m = +d.slice(1); } else { h = +d.slice(0, 2); m = +d.slice(2, 4); }
  if (h === 24 && m === 0) return "23:59";
  if (h > 23 || m > 59) return null;
  return `${pad(h)}:${pad(m)}`;
}

/** Linha do servidor → dia da tela: {dw, aberto, h24, periodos: [[abre, fecha], …]}. */
export function doServidor(h) {
  const periodos = [[h.opening_time || "08:00", h.closing_time || "22:00"]];
  if (h.has_second_period) periodos.push([h.opening_time_2 || "18:00", h.closing_time_2 || "23:59"]);
  if (h.has_second_period && h.has_third_period) periodos.push([h.opening_time_3 || "19:00", h.closing_time_3 || "23:59"]);
  return { dw: h.day_of_week, aberto: !!h.is_open, h24: !!h.is_24h, periodos };
}

const ordenar = (periodos) => [...periodos].sort((a, b) => paraMin(a[0]) - paraMin(b[0]));

/**
 * Dia da tela → item do PUT /business-hours. Campos de período que não estão
 * em uso guardam o que já estava gravado (`original`), para não perder nada.
 */
export function paraServidor(dia, original = {}) {
  const p = ordenar(dia.periodos);
  const aberto = dia.aberto && (dia.h24 || p.length > 0);
  const campo = (i, k, chave, padrao) => (p[i] ? p[i][k] : original[chave] || padrao);
  return {
    day_of_week: dia.dw,
    is_open: aberto,
    opening_time: campo(0, 0, "opening_time", "08:00"),
    closing_time: campo(0, 1, "closing_time", "22:00"),
    has_second_period: !dia.h24 && p.length >= 2,
    opening_time_2: campo(1, 0, "opening_time_2", "18:00"),
    closing_time_2: campo(1, 1, "closing_time_2", "23:59"),
    has_third_period: !dia.h24 && p.length >= 3,
    opening_time_3: campo(2, 0, "opening_time_3", "19:00"),
    closing_time_3: campo(2, 1, "closing_time_3", "23:59"),
    is_24h: aberto && !!dia.h24,
  };
}

/**
 * Período inválido: fecha antes de abrir (ou na mesma hora). O servidor compara
 * a hora dentro do mesmo dia, então 18:00 às 02:00 deixaria a loja fechada a
 * noite toda — a tela não deixa salvar e explica como fazer.
 */
export const periodoInvalido = ([abre, fecha]) => fimMin(fecha) <= paraMin(abre);

/** Faixas abertas do dia em minutos [início, fim], juntando as que se encostam. */
export function faixas(dia) {
  if (!dia?.aberto) return [];
  if (dia.h24) return [[0, 1440]];
  const lista = dia.periodos
    .filter((p) => !periodoInvalido(p))
    .map(([a, f]) => [paraMin(a), fimMin(f)])
    .sort((x, y) => x[0] - y[0]);
  const juntas = [];
  for (const f of lista) {
    const ultima = juntas[juntas.length - 1];
    if (ultima && f[0] <= ultima[1]) ultima[1] = Math.max(ultima[1], f[1]);
    else juntas.push([...f]);
  }
  return juntas;
}

export const totalMin = (lista) => lista.reduce((t, [s, e]) => t + e - s, 0);
export const sobreposicao = (lista, de, ate) => lista.reduce((t, [s, e]) => t + Math.max(0, Math.min(e, ate) - Math.max(s, de)), 0);

/** "11:00 às 15:00 e 18:00 às 23:59" / "Aberta 24 horas" / "Loja fechada". */
export function resumoDoDia(dia) {
  const f = faixas(dia);
  if (!f.length) return "Loja fechada";
  if (dia.h24) return "Aberta 24 horas";
  return f.map(([s, e]) => `${hm(s)} às ${hm(e)}`).join(" e ");
}

/** O próximo período sugerido ao tocar em "+ Adicionar horário". */
export function sugestaoDePeriodo(dia) {
  const ultimoFim = Math.max(0, ...dia.periodos.map((p) => fimMin(p[1])));
  if (!dia.periodos.length) return ["11:00", "15:00"];
  if (ultimoFim <= 18 * 60) return ["18:00", "23:00"];
  if (ultimoFim < 22 * 60) return [hm(ultimoFim + 30), "23:59"];
  return ["11:00", "15:00"];
}

/**
 * Horários da lista do campo, de 30 em 30 minutos (dono, 25/10/2026: "não faz
 * sentido fechar antes do horário de abertura"):
 *   fecha — só depois de `depoisDe` (a hora de abrir), terminando em 23:59;
 *   abre  — a partir de `aPartirDe` (o fechamento do horário anterior), sem 23:59.
 */
export function opcoesDeHora({ tipo = "abre", depoisDe = null, aPartirDe = null } = {}) {
  const lista = [];
  for (let m = 0; m < 1440; m += 30) lista.push(hm(m));
  if (tipo === "fecha") lista.push("23:59");
  return lista.filter((t) => {
    const min = fimMin(t);
    if (depoisDe && min <= paraMin(depoisDe)) return false;
    if (aPartirDe && min < fimMin(aPartirDe)) return false;
    return true;
  });
}

/** Algum dia aberto com período que fecha antes de abrir? */
export const temErro = (dias) => dias.some((d) => d.aberto && !d.h24 && d.periodos.some(periodoInvalido));

/** Pausa entre dois períodos ordenados, em minutos (0 se encostam ou se sobrepõem). */
export const pausaEntre = (anterior, proximo) => Math.max(0, paraMin(proximo[0]) - fimMin(anterior[1]));
